/**
 * ActivitiesFullCalendar - Componente principal do calendário de atividades
 * Implementa visões Dia, Semana e Mês usando FullCalendar
 * 
 * @version 1.1.0
 * RELEASE: 2026-02-20 v1.1.0 - Adicionado handler handleCancelBooking
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial do calendário completo
 */
import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import { useCalendarActivities } from '@/hooks/useCalendarActivities';
import { CalendarToolbar, type CalendarView } from './CalendarToolbar';
import { CalendarEventModal } from './CalendarEventModal';

import type { EventClickArg, DatesSetArg, DateSelectArg, EventDropArg } from '@fullcalendar/core';
import './calendar-styles.css';

interface ActivitiesFullCalendarProps {
  /** Callback ao clicar em um evento para abrir detalhes do negócio */
  onOpenDeal?: (dealId: string) => void;
  /** Data inicial do calendário */
  initialDate?: Date;
  /** Visão inicial */
  initialView?: CalendarView;
  /** Callback quando data é selecionada para criar atividade */
  onDateSelect?: (date: Date) => void;
  /** Filtro por workspace do negócio */
  workspaceId?: string | null;
  /** Filtro por vendedor do negócio */
  sellerId?: string | null;
  /** Filtro por responsável pela atividade */
  performedByUserId?: string | null;
  /** Filtro por status: all, open, completed */
  status?: string | null;
  /** Filtro por tipo de evento (event_type_id) - v1.7.0 */
  eventTypeId?: string | null;
  /** Eventos externos adicionais (ex: bookings standalone) já no formato EventInput */
  additionalEvents?: any[];
  /** Data controlada externamente (quando toolbar é gerenciada pelo pai) */
  externalDate?: Date;
  /** Visão controlada externamente */
  externalView?: CalendarView;
  /** Callback para sincronizar data de volta ao pai */
  onExternalDateChange?: (date: Date) => void;
  /** Callback para sincronizar visão de volta ao pai */
  onExternalViewChange?: (view: CalendarView) => void;
  /** Ocultar toolbar interna (quando controlada pelo pai) */
  hideToolbar?: boolean;
}

/**
 * Calendário completo de atividades com visões Dia/Semana/Mês
 */
export function ActivitiesFullCalendar({
  onOpenDeal,
  initialDate = new Date(),
  initialView = 'dayGridMonth',
  onDateSelect,
  workspaceId,
  sellerId,
  performedByUserId,
  status,
  eventTypeId,
  additionalEvents,
  externalDate,
  externalView,
  onExternalDateChange,
  onExternalViewChange,
  hideToolbar = false,
}: ActivitiesFullCalendarProps) {
  // Referência para a API do FullCalendar
  const calendarRef = useRef<FullCalendar>(null);
  const isMobile = useIsMobile();

  // Controle externo ou interno
  const isExternallyControlled = !!externalDate;

  // Estado da visão atual (usado quando controle é interno)
  const [internalView, setInternalView] = useState<CalendarView>(initialView);
  const [internalDate, setInternalDate] = useState(initialDate);
  const [calendarTitle, setCalendarTitle] = useState('');

  // Resolve valores efetivos (externo ou interno)
  const currentView = isExternallyControlled ? (externalView || initialView) : internalView;
  const currentDate = isExternallyControlled ? externalDate : internalDate;

  // Estado do modal de detalhes do evento
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // Calcula range de datas baseado na visão
  const getDateRange = useCallback(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    // Adiciona margem para incluir dias de semanas vizinhas
    return {
      startDate: startOfWeek(addDays(start, -7), { locale: ptBR }),
      endDate: endOfWeek(addDays(end, 7), { locale: ptBR }),
    };
  }, [currentDate]);

  const { startDate, endDate } = getDateRange();

  // Hook para buscar atividades
  const { 
    events, 
    isLoading, 
    completeActivity, 
    reopenActivity,
    updateActivityDate,
    refetch,
  cancelActivity,
  } = useCalendarActivities({
    startDate,
    endDate,
    workspaceId,
    sellerId,
    performedByUserId,
    status,
    eventTypeId,
  });

  // Handlers de navegação
  const handlePrev = () => {
    calendarRef.current?.getApi().prev();
  };

  const handleNext = () => {
    calendarRef.current?.getApi().next();
  };

  const handleToday = () => {
    calendarRef.current?.getApi().today();
  };

  /** Salta direto para uma data escolhida pelo DateRangeJumpPicker. */
  const handleJumpToDate = (date: Date) => {
    calendarRef.current?.getApi().gotoDate(date);
  };

  const handleViewChange = (view: CalendarView) => {
    if (isExternallyControlled) {
      onExternalViewChange?.(view);
    } else {
      setInternalView(view);
    }
    calendarRef.current?.getApi().changeView(view);
  };

  // Handler quando as datas visíveis mudam (sincroniza com pai ou estado interno)
  const handleDatesSet = (arg: DatesSetArg) => {
    if (isExternallyControlled) {
      onExternalDateChange?.(arg.view.currentStart);
    } else {
      setInternalDate(arg.view.currentStart);
    }
    setCalendarTitle(arg.view.title);
  };

  // Handler de clique em evento
  const handleEventClick = (arg: EventClickArg) => {
    setSelectedEvent(arg.event.extendedProps);
    setIsEventModalOpen(true);
  };

  // Handler de seleção de data/hora (para criar nova atividade)
  const handleDateSelect = (arg: DateSelectArg) => {
    onDateSelect?.(arg.start);
  };

  // Handler de drag & drop
  const handleEventDrop = (arg: EventDropArg) => {
    const activityId = arg.event.id;
    const newDate = arg.event.start;
    
    if (!newDate) return;

    updateActivityDate.mutate({ activityId, newDate }, {
      onError: () => {
        arg.revert(); // Reverte se falhar
      },
    });
  };

  // Handler para concluir atividade do modal
  const handleComplete = (activityId: string) => {
    completeActivity.mutate(activityId, {
      onSuccess: () => {
        setIsEventModalOpen(false);
        refetch();
      },
    });
  };

  // Handler para reabrir atividade do modal (com tratamento de conflito)
  const handleReopen = (activityId: string) => {
    reopenActivity.mutate(activityId, {
      onSuccess: () => {
        // Atualiza estado local do evento para refletir imediatamente no modal
        if (selectedEvent) {
          const desc = selectedEvent.description || '';
          const cleanDescription = desc.startsWith('❌ CANCELADO')
            ? desc.replace(/^❌ CANCELADO\n?/, '')
            : desc;

          setSelectedEvent({
            ...selectedEvent,
            isCompleted: false,
            bookingStatus: 'confirmed',
            description: cleanDescription,
            activity: {
              ...selectedEvent.activity,
              completed_at: null,
              description: cleanDescription,
            },
          });
        }
        refetch();
      },
      onError: (error: Error) => {
        if (error.message === 'SLOT_CONFLICT') {
          toast.error('Horário indisponível', {
            description: 'Este horário já está ocupado por outro agendamento. Considere criar um novo agendamento.',
          });
        } else {
          toast.error('Erro ao reabrir atividade');
        }
      },
    });
  };

  // Handler para cancelar agendamento online (v1.1.0) — com toast de feedback
  const handleCancelBooking = (cancelToken: string, reason?: string) => {
    cancelActivity.mutate({ cancelToken, reason }, {
      onSuccess: () => {
        // Atualiza estado local do evento para refletir cancelamento imediato (evita prefixo duplicado)
        if (selectedEvent) {
          const desc = selectedEvent.description || '';
          const prefixedDesc = desc.startsWith('❌ CANCELADO') ? desc : `❌ CANCELADO\n${desc}`;
          setSelectedEvent({
            ...selectedEvent,
            isCompleted: true,
            bookingStatus: 'cancelled',
            bookingCancelToken: null,
            description: prefixedDesc,
            activity: {
              ...selectedEvent.activity,
              completed_at: new Date().toISOString(),
              description: prefixedDesc,
            },
          });
        }
        toast.success('Agendamento cancelado com sucesso');
        refetch();
      },
      onError: (error: Error) => {
        toast.error('Erro ao cancelar agendamento', {
          description: error.message || 'Tente novamente mais tarde',
        });
      },
    });
  };

  // Handler para abrir negócio
  const handleOpenDeal = (dealId: string) => {
    setIsEventModalOpen(false);
    onOpenDeal?.(dealId);
  };

  // Sincroniza FullCalendar quando data/visão mudam externamente
  React.useEffect(() => {
    if (!isExternallyControlled) return;
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (externalView && api.view.type !== externalView) {
      api.changeView(externalView);
    }
    if (externalDate && api.getDate().getTime() !== externalDate.getTime()) {
      api.gotoDate(externalDate);
    }
  }, [isExternallyControlled, externalDate, externalView]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar Customizada — oculta quando controlada externamente */}
      {!hideToolbar && (
        <CalendarToolbar
          title={calendarTitle}
          currentDate={currentDate}
          currentView={currentView}
          onPrev={handlePrev}
          onNext={handleNext}
          onToday={handleToday}
          onViewChange={handleViewChange}
          onJumpToDate={handleJumpToDate}
        />
      )}

      {/* Calendário FullCalendar */}
      <div className="flex-1 min-h-0 relative">
        {/* Loading overlay não-destrutivo - mantém calendário montado */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10 pointer-events-none">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Carregando...</span>
            </div>
          </div>
        )}
        
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={initialView}
          initialDate={currentDate}
          events={additionalEvents ? additionalEvents : events}
          locale="pt-br"
          headerToolbar={false}
          height="100%"
          dayMaxEvents={isMobile ? 2 : 3}
          moreLinkText={(num) => `+${num} mais`}
          allDaySlot={false}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:30:00"
          nowIndicator={true}
          selectable={true}
          selectMirror={true}
          editable={true}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          select={handleDateSelect}
          eventDrop={handleEventDrop}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }}
          /* No mobile: header curto (D, S, T...) para caber 7 colunas */
          dayHeaderFormat={isMobile
            ? { weekday: 'narrow' }
            : { weekday: 'short', day: 'numeric' }
          }
          firstDay={0}
          buttonText={{
            today: 'Hoje',
            month: 'Mês',
            week: 'Semana',
            day: 'Dia',
          }}
        />
      </div>

      {/* Modal de Detalhes do Evento */}
      <CalendarEventModal
        open={isEventModalOpen}
        onOpenChange={setIsEventModalOpen}
        event={selectedEvent}
        onComplete={handleComplete}
        onReopen={handleReopen}
        onOpenDeal={handleOpenDeal}
        onCancelBooking={handleCancelBooking}
        isCompleting={completeActivity.isPending}
        isReopening={reopenActivity.isPending}
        isCancelling={cancelActivity.isPending}
      />
    </div>
  );
}
